import {
  Box,
  ButtonBase,
  Dialog,
  Drawer,
  Fade,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { CopyIcon } from "../../../icons";
import { formatCardNumber, UserCard } from "../../../services/cards/mocks";
import type { CardSensitiveDetails } from "../../../services/cards";

interface CardCredentialsModalProps {
  open: boolean;
  onClose: () => void;
  card: Pick<UserCard, "expiry" | "cardholderName">;
  details: CardSensitiveDetails | null;
}

/**
 * Модалка с реквизитами карты (PAN / CVV / expiry / holder).
 *
 * Респонсив:
 *   - **Мобиль** (< md) — Drawer, всплывает снизу, ручка сверху. Ту же
 *     стилистику используют `ChangePinDrawer` / `RevealPinDrawer`, чтобы
 *     флоу PIN → реквизиты был визуально непрерывным.
 *   - **Десктоп** (>= md) — центрированный Dialog с полями и «Закрыть»
 *     внизу.
 *
 * Модалка stateless: реквизиты приходят пропсами. Родитель отвечает за
 * очистку `details` при закрытии, чтобы следующий показ снова требовал PIN.
 */
export function CardCredentialsModal({
  open,
  onClose,
  card,
  details,
}: CardCredentialsModalProps) {
  const { t } = useTranslation("cards");
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  const handleCopy = (text: string | undefined, label: string) => {
    if (!text) return;
    navigator.clipboard?.writeText(text);
    enqueueSnackbar(t("issue.copied", { label }), { variant: "success" });
  };

  const content = (
    <Box>
      {/* Handle bar — только на drawer-варианте */}
      {!isDesktop && (
        <Box
          sx={(t2) => ({
            width: "44px",
            height: "4px",
            borderRadius: "4px",
            bgcolor:
              t2.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.2)"
                : "rgba(0, 0, 0, 0.2)",
            margin: "0 auto 14px",
          })}
        />
      )}

      <Fade in timeout={200}>
        <Box>
          <Typography
            sx={{
              fontSize: "20px",
              fontWeight: 600,
              textAlign: isDesktop ? "left" : "center",
              marginBottom: "4px",
              color: "text.primary",
            }}
          >
            {t("credentials.title")}
          </Typography>
          <Typography
            sx={{
              fontSize: "13px",
              color: "text.secondary",
              textAlign: isDesktop ? "left" : "center",
              marginBottom: "18px",
              lineHeight: 1.5,
            }}
          >
            {t("credentials.modal_hint")}
          </Typography>

          {!details ? (
            <Typography
              sx={{
                fontSize: "13px",
                color: "text.secondary",
                paddingY: "8px",
              }}
            >
              {t("credentials.reveal_failed")}
            </Typography>
          ) : details.mode === "iframe" ? (
            // PAN delegation (Airwallex): PAN/CVV Airwallex рисует внутри
            // своего PCI-iframe — мы даже не видим их значений. Стили внутри
            // iframe заранее заданы бэкендом в hash-фрагменте URL.
            <Box
              sx={(t2) => ({
                borderRadius: "14px",
                overflow: "hidden",
                border: "1px solid",
                borderColor:
                  t2.palette.mode === "dark"
                    ? "rgba(255,255,255,0.08)"
                    : "rgba(0,0,0,0.08)",
                background:
                  t2.palette.mode === "dark"
                    ? "rgba(255,255,255,0.02)"
                    : "rgba(0,0,0,0.02)",
              })}
            >
              <iframe
                title="card-details"
                src={details.iframe_url}
                style={{
                  width: "100%",
                  height: "220px",
                  border: "none",
                  display: "block",
                  background: "transparent",
                }}
                // sandbox без allow-same-origin — Airwallex-домен внутри,
                // но нашему origin'у доступ к DOM iframe не нужен.
                sandbox="allow-scripts allow-forms allow-popups"
                referrerPolicy="no-referrer"
              />
              {card.cardholderName && (
                <Box
                  sx={(t2) => ({
                    padding: "10px 14px",
                    borderTop: "1px solid",
                    borderColor:
                      t2.palette.mode === "dark"
                        ? "rgba(255,255,255,0.08)"
                        : "rgba(0,0,0,0.08)",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: "12px",
                  })}
                >
                  <Typography
                    sx={{ fontSize: "13px", color: "text.secondary" }}
                  >
                    {t("detail.cardholder")}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "14px",
                      fontWeight: 500,
                      color: "text.primary",
                    }}
                  >
                    {card.cardholderName}
                  </Typography>
                </Box>
              )}
            </Box>
          ) : (
            <Box
              sx={{
                "& > *:not(:last-child)": {
                  borderBottom: "1px solid",
                  borderColor: "divider",
                },
              }}
            >
              <CredentialRow
                label={t("detail.number")}
                value={formatCardNumber(details.pan)}
                onCopy={() => handleCopy(details.pan, t("issue.card_number"))}
              />
              <CredentialRow
                label={t("detail.exp")}
                value={details.expiry || card.expiry}
                onCopy={() =>
                  handleCopy(details.expiry || card.expiry, t("detail.exp"))
                }
              />
              <CredentialRow
                label={t("detail.cvv")}
                value={details.cvv}
                onCopy={() => handleCopy(details.cvv, t("detail.cvv"))}
              />
              <CredentialRow
                label={t("detail.cardholder")}
                value={details.cardholder_name || card.cardholderName}
              />
            </Box>
          )}

          <ButtonBase
            onClick={onClose}
            sx={(t2) => ({
              width: "100%",
              marginTop: "18px",
              padding: "12px",
              borderRadius: "14px",
              fontSize: "14px",
              fontWeight: 500,
              color: "text.secondary",
              bgcolor:
                t2.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.04)"
                  : "rgba(0, 0, 0, 0.04)",
              "&:hover, &:active": {
                bgcolor:
                  t2.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.08)"
                    : "rgba(0, 0, 0, 0.08)",
              },
            })}
          >
            {t("credentials.close")}
          </ButtonBase>
        </Box>
      </Fade>
    </Box>
  );

  if (isDesktop) {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="xs"
        fullWidth
        slotProps={{
          backdrop: { sx: { backgroundColor: "rgba(0, 0, 0, 0.55)" } },
        }}
        PaperProps={{
          sx: {
            backgroundColor: "secondary.main",
            borderRadius: "24px",
            padding: "24px",
            backgroundImage: "none",
          },
        }}
      >
        {content}
      </Dialog>
    );
  }

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      sx={{ zIndex: 100010 }}
      slotProps={{
        backdrop: { sx: { backgroundColor: "rgba(0, 0, 0, 0.55)" } },
      }}
      PaperProps={{
        sx: {
          backgroundColor: "secondary.main",
          borderTopLeftRadius: "24px",
          borderTopRightRadius: "24px",
          padding: "10px 16px 24px",
          backgroundImage: "none",
        },
      }}
    >
      {content}
    </Drawer>
  );
}

// ---------- Атом строки key–value ----------

interface CredentialRowProps {
  label: string;
  value: string;
  onCopy?: () => void;
}

function CredentialRow({ label, value, onCopy }: CredentialRowProps) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "12px",
        paddingY: "12px",
      }}
    >
      <Typography sx={{ fontSize: "13px", color: "text.secondary", flexShrink: 0 }}>
        {label}
      </Typography>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          minWidth: 0,
        }}
      >
        <Typography
          sx={{
            fontSize: "15px",
            fontWeight: 500,
            fontFeatureSettings: "'tnum'",
            letterSpacing: "0.4px",
            color: "text.primary",
            textAlign: "right",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {value}
        </Typography>
        {onCopy && (
          <ButtonBase
            onClick={onCopy}
            sx={{
              color: "primary.main",
              flexShrink: 0,
              display: "flex",
              padding: "4px",
              borderRadius: "8px",
              "&:active": { opacity: 0.7 },
            }}
          >
            <CopyIcon />
          </ButtonBase>
        )}
      </Box>
    </Box>
  );
}
